/**
 * Hooks for the curated Steam-Gifts purchase flow.
 *
 * Catalogue is **search-driven** — we never load the full apps list
 * up front. The SPA hits `/web/api/steam_gift/search?q=...` (debounced)
 * and then `/steam_gift/app/{app_id}` only when the user picks a card.
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import { api, ApiError } from "./client";

// ---- search --------------------------------------------------------------

export interface SteamGiftSearchHit {
  app_id: number;
  name: string;
}

interface SteamGiftSearchResponse {
  items: SteamGiftSearchHit[];
}

export function useSearchSteamGifts(query: string, enabled = true) {
  const trimmed = query.trim();
  return useQuery<SteamGiftSearchResponse, ApiError>({
    queryKey: ["steam_gift", "search", trimmed],
    queryFn: () =>
      api.get<SteamGiftSearchResponse>(
        `/web/api/steam_gift/search?q=${encodeURIComponent(trimmed)}`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

// ---- app detail ----------------------------------------------------------

export interface SteamGiftSubRegionPrice {
  region: string;
  region_price: number;
  /** USD price the calling user would pay (operator cost × their markup). */
  usd_cost: number;
}

export interface SteamGiftSub {
  sub_id: number;
  name: string | null;
  regions: SteamGiftSubRegionPrice[];
}

export interface SteamGiftAppDetail {
  app_id: number;
  name: string;
  /** Live URL from Steam's appdetails API; null on outage. */
  header_image: string | null;
  subs: SteamGiftSub[];
}

export function useSteamGiftAppDetail(appId: number | null) {
  return useQuery<SteamGiftAppDetail, ApiError>({
    queryKey: ["steam_gift", "app", appId],
    queryFn: () =>
      api.get<SteamGiftAppDetail>(`/web/api/steam_gift/app/${appId}`),
    enabled: appId !== null,
    staleTime: 30_000,
  });
}

// ---- buy -----------------------------------------------------------------

export interface SteamGiftBuyParams {
  service_id: number;
  sub_id: number;
  region: string;
  friend_link: string;
  gift_name: string;
  gift_description?: string;
  /** TOTP code, when the user has 2FA gated on purchases. */
  totp_code?: string;
}

// Wraps create_order + pay_order into one flow caller.
export interface SteamGiftBuyResult {
  custom_id: string;
  status: "completed" | "refunded" | "insufficient" | "in_progress";
  balance: string;
}

export function useBuySteamGift() {
  return useMutation<SteamGiftBuyResult, ApiError, SteamGiftBuyParams>({
    mutationFn: async (params) => {
      const customId = crypto.randomUUID();
      await api.post<{ custom_id: string }>("/web/api/orders/create", {
        body: {
          service_id: params.service_id,
          custom_id: customId,
          fields: [
            { key: "region", value: params.region },
            { key: "sub_id", value: params.sub_id },
            { key: "friendLink", value: params.friend_link },
            { key: "giftName", value: params.gift_name },
            {
              key: "giftDescription",
              value: params.gift_description ?? "",
            },
          ],
        },
      });
      const pay = await api.post<{
        custom_id: string;
        status: SteamGiftBuyResult["status"];
        balance: string;
      }>("/web/api/orders/pay", {
        body: {
          custom_id: customId,
          ...(params.totp_code ? { totp_code: params.totp_code } : {}),
        },
      });
      return {
        custom_id: customId,
        status: pay.status,
        balance: pay.balance,
      };
    },
  });
}

// ---- service id resolver ------------------------------------------------

export interface SteamGiftService {
  service_id: number;
  service_name: string | null;
}

export function useSteamGiftService() {
  return useQuery<SteamGiftService, ApiError>({
    queryKey: ["steam_gift", "service"],
    queryFn: () => api.get<SteamGiftService>("/web/api/steam_gift/service"),
    staleTime: 5 * 60_000,
  });
}

// ---- helpers -------------------------------------------------------------

export function steamHeaderUrl(appId: number): string {
  // Steam's CDN convention. cdn.akamai.steamstatic.com is the
  // canonical host used by store.steampowered.com and works without
  // auth or CORS issues for hot-linking.
  return `https://cdn.akamai.steamstatic.com/steam/apps/${appId}/header.jpg`;
}
